import { useEffect, useMemo, useRef, useState, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ProductCardSkeleton } from '../components/ProductCardSkeleton';
import { Testimonials } from '../components/Testimonials';
import { ShareMenu } from '../components/ShareMenu';
import { Product, ProductCategory } from '../types';
import { getProducts, resolveImageUrl } from '../lib/api';
import { resolveLocalized } from '../lib/localizedProduct';
import { useCurrency } from '../currency/CurrencyContext';
import { SEO } from '../seo/SEO';

const SHARE_ORIGIN = 'https://faticaftan.com';

function absoluteShareImage(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const resolved = resolveImageUrl(raw);
  return /^https?:\/\//i.test(resolved) ? resolved : undefined;
}

/**
 * Cinematic home — full-bleed hero + 4 category sections with horizontal
 * scroll-snap carousels (Caftans / Takchitas / Djellabas / Prêt-à-porter).
 *
 * Variant E port: the search / sort / filter strip from the previous editorial
 * grid was dropped in favour of category browsing. Discovery now happens by
 * scrolling rows; appointment / WhatsApp reservation flows live on the product
 * detail page.
 */
export function HomePage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProducts() {
      try {
        const data = await getProducts();
        setProducts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch products');
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  const grouped = useMemo(() => {
    const custom = products.filter((p) => p.productType !== 'READY_TO_WEAR');
    return {
      caftan: custom.filter((p) => (p.category ?? 'OTHER') === 'CAFTAN'),
      takchita: custom.filter((p) => (p.category ?? 'OTHER') === 'TAKCHITA'),
      djellaba: custom.filter((p) => (p.category ?? 'OTHER') === 'DJELLABA'),
      readyToWear: products.filter((p) => p.productType === 'READY_TO_WEAR'),
    };
  }, [products]);

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-red-700 font-display italic">{error}</div>
      </div>
    );
  }

  return (
    <>
      <SEO titleKey="seo.home.title" descriptionKey="seo.home.description" canonicalPath="/" />

      {/* ─────────── HERO ─────────── */}
      <section className="cinematic-hero">
        <div className="cinematic-hero-img" />
        <div className="cinematic-hero-overlay" />
        <div className="cinematic-container cinematic-hero-content">
          <span className="cinematic-hero-kicker">{t('hero.kicker')}</span>
          <h1>
            {t('hero.titleLine1')} <b>{t('hero.titleAccent1')}</b>
            {t('hero.titleLine2')}
            <br />
            {t('hero.titleLine3')} <i>{t('hero.titleAccent2')}</i>
          </h1>
          <p className="lede">{t('hero.lede')}</p>
          <div className="cinematic-hero-actions">
            <Link to="/contact" className="cinematic-btn cinematic-btn-gold">
              {t('hero.ctaPrimary')} →
            </Link>
            <a href="#caftans" className="cinematic-btn cinematic-btn-ghost">
              {t('hero.ctaSecondary')}
            </a>
          </div>
        </div>
        <div className="cinematic-hero-meta">
          <div className="ref">
            {t('hero.refNumber')}
            <small>{t('hero.refLabel')}</small>
          </div>
        </div>
      </section>

      {/* ─────────── CATEGORIES ─────────── */}
      {loading ? (
        <section className="cat-section">
          <div className="cinematic-container">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {Array.from({ length: 3 }).map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </section>
      ) : (
        <>
          <CategoryRow id="caftans" sectionKey="caftan" products={grouped.caftan} />
          <CategoryRow id="takchitas" sectionKey="takchita" products={grouped.takchita} />
          <CategoryRow id="djellabas" sectionKey="djellaba" products={grouped.djellaba} />
          <CategoryRow id="pap" sectionKey="readyToWear" products={grouped.readyToWear} showDivider={false} />
        </>
      )}

      {/* ─────────── ATELIER ─────────── */}
      <section className="atelier-section" id="atelier">
        <div className="atelier-grid">
          <div className="atelier-img-wrap" aria-hidden="true" />
          <div className="atelier-text">
            <span className="cinematic-hero-kicker" style={{ marginBottom: 28 }}>
              {t('atelier.kicker')}
            </span>
            <h2>
              {t('atelier.titleLine1')} <b>{t('atelier.titleAccent')}</b>
              {t('atelier.titleLine2')}
            </h2>
            <p>{t('atelier.body1')}</p>
            <p>{t('atelier.body2')}</p>
            <div className="atelier-stats">
              <div className="atelier-stat">
                <div className="v">{t('atelier.stats.yearsValue')}</div>
                <div className="k">{t('atelier.stats.yearsLabel')}</div>
              </div>
              <div className="atelier-stat">
                <div className="v">{t('atelier.stats.piecesValue')}</div>
                <div className="k">{t('atelier.stats.piecesLabel')}</div>
              </div>
              <div className="atelier-stat">
                <div className="v">{t('atelier.stats.citiesValue')}</div>
                <div className="k">{t('atelier.stats.citiesLabel')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── TESTIMONIALS ─────────── */}
      <Testimonials />

      {/* ─────────── WHATSAPP STRIP ─────────── */}
      <section className="wa-strip">
        <div className="cinematic-container wa-inner">
          <div className="wa-icon-big" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01zM12.04 20.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.12.82.83-3.04-.19-.31a8.21 8.21 0 0 1-1.26-4.37c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.69 8.24-8.24 8.24z" />
            </svg>
          </div>
          <div>
            <h3>
              {t('waStrip.titleLine1')} <b>{t('waStrip.titleAccent')}</b>
              {t('waStrip.titleLine2')}
            </h3>
            <p>{t('waStrip.subtitle')}</p>
          </div>
          <a
            href="https://wa.me/212701048661"
            className="wa-btn-noir"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('waStrip.cta')}
          </a>
        </div>
      </section>
    </>
  );
}

type SectionKey = 'caftan' | 'takchita' | 'djellaba' | 'readyToWear';

interface CategoryRowProps {
  id: string;
  sectionKey: SectionKey;
  products: Product[];
  showDivider?: boolean;
}

function CategoryRow({ id, sectionKey, products, showDivider = true }: CategoryRowProps) {
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement>(null);

  const base = `catalog.sections.${sectionKey}`;

  function scrollBy(dir: 1 | -1) {
    const el = scrollerRef.current;
    if (!el || typeof el.scrollBy !== 'function') return;
    const card = el.querySelector('.pcard') as HTMLElement | null;
    const step = card ? card.offsetWidth + 24 : 320;
    el.scrollBy({ left: dir * step * 2, behavior: 'smooth' });
  }

  return (
    <section className="cat-section" id={id}>
      <div className="cat-head">
        <div className="cat-num">
          {t(`${base}.num`)}
          <sup>{t(`${base}.numLabel`)}</sup>
        </div>
        <div className="cat-title">
          <h2>
            {t(`${base}.titleLine1`)} <b>{t(`${base}.titleAccent`)}</b>
            {t(`${base}.titleLine2`)}
          </h2>
          <div className="sub">{t(`${base}.subtitle`)}</div>
        </div>
        <div className="cat-meta">
          <b>
            {products.length} {t(`${base}.countLabel`)}
          </b>
          {t(`${base}.metaLine1`)}
          <br />
          {t(`${base}.metaLine2`)}
        </div>
      </div>

      {products.length === 0 ? (
        <div className="cat-empty">{t('catalog.sections.emptyCategory')}</div>
      ) : (
        <div className="cat-scroller-wrap">
          <button
            type="button"
            className="cat-arrow left"
            onClick={() => scrollBy(-1)}
            aria-label={t('catalog.scrollPrev')}
          >
            ‹
          </button>
          <button
            type="button"
            className="cat-arrow right"
            onClick={() => scrollBy(1)}
            aria-label={t('catalog.scrollNext')}
          >
            ›
          </button>
          <div className="cat-scroller" ref={scrollerRef as RefObject<HTMLDivElement>}>
            {products.map((product, idx) => (
              <PCard key={product.id} product={product} index={idx} sectionKey={sectionKey} />
            ))}
          </div>
        </div>
      )}

      {showDivider && (
        <div className="cat-divider">
          <hr />
        </div>
      )}
    </section>
  );
}

interface PCardProps {
  product: Product;
  index: number;
  sectionKey: SectionKey;
}

function PCard({ product, index, sectionKey }: PCardProps) {
  const { t, i18n } = useTranslation();
  const { format } = useCurrency();
  const { title, description } = resolveLocalized(product, i18n.language);
  const detailHref = `/products/${product.id}`;
  const num = `N°${String(index + 1).padStart(2, '0')}`;
  const tagKey =
    sectionKey === 'readyToWear'
      ? 'product.readyToWearBadge'
      : `catalog.category.values.${categoryLabel(product.category)}`;

  return (
    <div className="pcard-wrap">
      <Link to={detailHref} className="pcard" aria-label={title}>
        <div className="pcard-img">
          <span className="pcard-num">{num}</span>
          <span className="pcard-tag">{t(tagKey)}</span>
          <img src={resolveImageUrl(product.imageUrl)} alt={title} loading="lazy" />
        </div>
        <div className="pcard-info">
          <div className="pcard-name" dir="auto">{title}</div>
          <div className="pcard-price">{format(product.price)}</div>
        </div>
        <div className="pcard-detail" dir="auto">{description}</div>
        <span className="pcard-cta">{t('product.reserveButton')}</span>
      </Link>
      <div className="pcard-share-overlay">
        <ShareMenu
          variant="icon"
          url={`${SHARE_ORIGIN}/products/${product.id}`}
          title={product.title}
          description={product.description.slice(0, 200)}
          image={absoluteShareImage(product.imageUrl)}
        />
      </div>
    </div>
  );
}

function categoryLabel(cat: ProductCategory | undefined): 'CAFTAN' | 'TAKCHITA' | 'DJELLABA' | 'OTHER' {
  if (cat === 'CAFTAN' || cat === 'TAKCHITA' || cat === 'DJELLABA') return cat;
  return 'OTHER';
}
